import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { GraduationCap, Eye, EyeOff, Gift, Phone, ArrowLeft, CheckCircle } from 'lucide-react';
import { validateReferralCode } from '../../services/authApi';
import apiClient from '../../services/apiClient';

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [checkingReferral, setCheckingReferral] = useState(false);

  // OTP state
  const [step, setStep] = useState(1); // 1 = form, 2 = OTP verification
  const [otpSending, setOtpSending] = useState(false);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'student'
  });

  // Timer countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Resend cooldown
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => setResendCooldown(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const checkReferralCode = async () => {
    if (!referralCode.trim()) {
      setReferralValid(null);
      return;
    }

    setCheckingReferral(true);
    try {
      const result = await validateReferralCode(referralCode);
      setReferralValid(result);
    } catch (error) {
      setReferralValid({ valid: false, message: 'Failed to validate code' });
    } finally {
      setCheckingReferral(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Step 1 → Send OTP
  const handleSendOtp = async () => {
    setError('');

    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setOtpSending(true);
    try {
      await apiClient.post('/auth/send-otp', { phone: formData.phone });
      setStep(2);
      setTimer(300); // 5 minutes
      setResendCooldown(30); // 30 second cooldown before resend
      setOtpValues(['', '', '', '', '', '']);
      // Focus first OTP input after transition
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setOtpSending(true);
    try {
      await apiClient.post('/auth/send-otp', { phone: formData.phone });
      setTimer(300);
      setResendCooldown(30);
      setOtpValues(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP.');
    } finally {
      setOtpSending(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // only digits

    const newValues = [...otpValues];
    newValues[index] = value.slice(-1); // single digit
    setOtpValues(newValues);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // On backspace, move to previous input
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newValues = [...otpValues];
      for (let i = 0; i < 6; i++) {
        newValues[i] = pasted[i] || '';
      }
      setOtpValues(newValues);
      // Focus the input after the last pasted digit
      const focusIndex = Math.min(pasted.length, 5);
      otpInputRefs.current[focusIndex]?.focus();
    }
  };

  // Verify OTP then register
  const handleVerifyAndRegister = async () => {
    const otpCode = otpValues.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setError('');
    setOtpVerifying(true);

    try {
      // Step 1: Verify OTP
      const verifyRes = await apiClient.post('/auth/verify-otp', {
        phone: formData.phone,
        otp: otpCode
      });

      const token = verifyRes.otp_token;
      setOtpToken(token);
      setPhoneVerified(true);

      // Step 2: Register with the OTP token
      setLoading(true);
      const result = await register({
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: formData.role
      }, referralCode || null, token);

      if (result.success) {
        if (result.user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error);
        setPhoneVerified(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'OTP verification failed. Please try again.');
      setPhoneVerified(false);
    } finally {
      setOtpVerifying(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#041f96]/5 via-white to-[#041f96]/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img src="https://customer-assets.emergentagent.com/job_tutor-contact-access/artifacts/ofobdr0d_AVYUDHA_LOGO_2-removebg-preview.png" alt="Avyudha" className="h-14 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {step === 1 ? 'Create Account' : 'Verify Phone Number'}
          </CardTitle>
          <CardDescription>
            {step === 1
              ? 'Join Avyudha and start your learning journey'
              : `We've sent a 6-digit OTP to ${formData.phone}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 1 ? (
            /* ===== STEP 1: Registration Form ===== */
            <form onSubmit={(e) => { e.preventDefault(); handleSendOtp(); }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">I am a</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="tutor">Tutor</SelectItem>
                    <SelectItem value="organization">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              {formData.role !== 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="referralCode">
                    <div className="flex items-center space-x-2">
                      <Gift className="h-4 w-4 text-blue-600" />
                      <span>Referral Code (Optional)</span>
                    </div>
                  </Label>
                  <div className="flex space-x-2">
                    <Input
                      id="referralCode"
                      name="referralCode"
                      type="text"
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value.toUpperCase());
                        setReferralValid(null);
                      }}
                      onBlur={checkReferralCode}
                    />
                    {checkingReferral && (
                      <Button type="button" disabled variant="outline">
                        Checking...
                      </Button>
                    )}
                  </div>
                  {referralValid && (
                    <p className={`text-sm ${referralValid.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {referralValid.message}
                      {referralValid.valid && ' - You\'ll get 10 bonus coins!'}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#041f96] hover:bg-[#031580]"
                disabled={otpSending}
              >
                {otpSending ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Sending OTP...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Verify Phone & Create Account
                  </span>
                )}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#041f96] hover:underline font-medium">
                  Login
                </Link>
              </p>
            </form>
          ) : (
            /* ===== STEP 2: OTP Verification ===== */
            <div className="space-y-6">
              {/* Back button */}
              <button
                onClick={() => { setStep(1); setError(''); }}
                className="flex items-center text-sm text-gray-500 hover:text-gray-800 transition"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Change phone number
              </button>

              {/* OTP Input Fields */}
              <div className="flex justify-center gap-2 sm:gap-3">
                {otpValues.map((val, i) => (
                  <input
                    key={i}
                    ref={el => otpInputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-bold border-2 rounded-lg
                      focus:border-[#041f96] focus:ring-2 focus:ring-[#041f96]/20 outline-none
                      transition-all duration-200"
                    style={{ fontSize: '1.25rem' }}
                  />
                ))}
              </div>

              {/* Timer */}
              {timer > 0 ? (
                <p className="text-center text-sm text-gray-500">
                  OTP expires in <span className="font-semibold text-[#041f96]">{formatTime(timer)}</span>
                </p>
              ) : (
                <p className="text-center text-sm text-red-500 font-medium">
                  OTP expired. Please request a new one.
                </p>
              )}

              {/* Resend */}
              <div className="text-center">
                <button
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || otpSending}
                  className={`text-sm font-medium ${resendCooldown > 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[#041f96] hover:underline cursor-pointer'
                    }`}
                >
                  {otpSending
                    ? 'Sending...'
                    : resendCooldown > 0
                      ? `Resend OTP in ${resendCooldown}s`
                      : 'Resend OTP'}
                </button>
              </div>

              {/* Verify Button */}
              <Button
                onClick={handleVerifyAndRegister}
                className="w-full bg-[#041f96] hover:bg-[#031580]"
                disabled={otpVerifying || loading || otpValues.join('').length !== 6 || timer === 0}
              >
                {otpVerifying || loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {otpVerifying ? 'Verifying OTP...' : 'Creating Account...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Verify & Create Account
                  </span>
                )}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#041f96] hover:underline font-medium">
                  Login
                </Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
